/**
 * Content System Initialization
 *
 * Public registry-style API for content loading:
 * - ensureAllContentLoaded() — load all 10 modules (idempotent, retry-on-failure)
 * - ensureModuleLoaded(moduleCode) — load one module (idempotent, retry-on-failure)
 * - isContentLoaded() — synchronous status check
 * - initializeContentSystem() — back-compat alias for ensureAllContentLoaded()
 *
 * Bundle Optimization Phase 2 — mei 2026.
 * Refactored to per-module registry — Boot Flow Refactor (mei 2026).
 */

import {
  initializeSeedCollections,
  ensureModuleSeedsLoaded,
  isSeedSystemReady,
  SEED_COLLECTIONS,
} from './seedConsolidator'
import { initializeQuestionLoader, addModuleToIndexes } from './questionLoader'
import { MODULE_CODE_TO_ID, MODULE_ID_TO_CODE } from '../modules'
import type { ModuleId } from '../types'
import { debug, error as logError } from '../../utils/logger'

// ── Status flags ─────────────────────────────────────────────────────

let _allContentReady = false

/** Synchronous check — true once all 10 modules have been loaded + indexed. */
export function isContentLoaded(): boolean {
  return _allContentReady
}

/** Synchronous check — true if a specific module is loaded + indexed. */
export function isModuleContentLoaded(moduleCode: string): boolean {
  const moduleId = MODULE_CODE_TO_ID[moduleCode] as ModuleId | undefined
  return Boolean(moduleId && SEED_COLLECTIONS.has(moduleId))
}

// ── Per-module promise cache (with retry on failure) ─────────────────

const _modulePromises = new Map<string, Promise<void>>()

/**
 * Ensure a single module's seeds + indexes are loaded.
 * Idempotent. On failure, the cached promise is cleared so the caller
 * can retry by calling again.
 */
export function ensureModuleLoaded(moduleCode: string): Promise<void> {
  if (isModuleContentLoaded(moduleCode)) return Promise.resolve()

  const cached = _modulePromises.get(moduleCode)
  if (cached) return cached

  const promise = (async () => {
    const collection = await ensureModuleSeedsLoaded(moduleCode)
    if (collection) {
      addModuleToIndexes(collection.module)
    }
  })().catch((err) => {
    // Clear cached promise so future calls can retry
    _modulePromises.delete(moduleCode)
    logError(`❌ Failed to load module ${moduleCode}:`, err)
    throw err
  })

  _modulePromises.set(moduleCode, promise)
  return promise
}

// ── Whole-content load (with retry) ──────────────────────────────────

let _allContentPromise: Promise<void> | null = null

/**
 * Ensure all 10 modules are loaded + indexed.
 * Idempotent. Loads all modules in parallel.
 * On failure, the cached promise is cleared so callers can retry.
 */
export function ensureAllContentLoaded(): Promise<void> {
  if (_allContentReady) return Promise.resolve()
  if (_allContentPromise) return _allContentPromise

  _allContentPromise = (async () => {
    const start = performance.now()

    // Step 1+2: Load all seeds and build collections (single batched import)
    await initializeSeedCollections()

    // Step 3: Build all indexes
    await initializeQuestionLoader()

    _allContentReady = true

    const elapsed = Math.round(performance.now() - start)
    debug(`✅ Content system initialized in ${elapsed}ms`)
  })().catch((err) => {
    _allContentPromise = null
    logError('❌ Content system initialization failed:', err)
    throw err
  })

  return _allContentPromise
}

/**
 * @deprecated Use ensureAllContentLoaded() — kept for back-compat.
 */
export async function initializeContentSystem(): Promise<void> {
  return ensureAllContentLoaded()
}

// ── Idle prefetch helper ─────────────────────────────────────────────

/**
 * Schedule a content load during browser idle time.
 * Used after first paint to warm the cache without blocking the UI.
 *
 * Falls back to setTimeout if requestIdleCallback is unavailable.
 */
export function prefetchAllContentWhenIdle(): void {
  if (_allContentReady || _allContentPromise) return

  const schedule = (cb: () => void) => {
    if (typeof window === 'undefined') return
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined
    if (ric) {
      ric(cb, { timeout: 3000 })
    } else {
      window.setTimeout(cb, 1500)
    }
  }

  schedule(() => {
    ensureAllContentLoaded().catch(() => {
      /* non-fatal — will retry on next consumer call */
    })
  })
}

// ── Re-exports for diagnostics ───────────────────────────────────────

export { MODULE_ID_TO_CODE, MODULE_CODE_TO_ID }

/**
 * Diagnostic snapshot of the content-loading status.
 * Safe to call at any time. Useful for an admin diagnostics widget.
 */
export function getContentLoadStatus(): {
  allLoaded: boolean
  totalModules: number
  loadedModuleCodes: string[]
  pendingModuleCodes: string[]
} {
  const all = Object.keys(MODULE_CODE_TO_ID)
  const loaded = all.filter((code) => isModuleContentLoaded(code))
  const pending = all.filter((code) => !isModuleContentLoaded(code))
  return {
    allLoaded: _allContentReady,
    totalModules: all.length,
    loadedModuleCodes: loaded,
    pendingModuleCodes: pending,
  }
}
